import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { Activity, BarChart3, Bell, BookOpen, BookText, Briefcase, Calendar, Clapperboard, FileText, Globe, Home, Image, LineChart, LogOut, Mail, MapPin, Menu, MessageCircle, Package, PoundSterling, Rss, Search, Shield, ShieldCheck, Sparkles, Trophy, Users, X } from 'lucide-react';
import api from '../lib/api';
import BrandLogo from '../components/BrandLogo';
import BackendHealthBanner from './BackendHealthBanner';

const NAV = [
  { to: '/admin', label: 'Overview', icon: Home, end: true },
  { to: '/admin/guide', label: 'Admin Guide', icon: BookOpen },
  { to: '/admin/lester', label: 'Lester Chatbot', icon: MessageCircle },
  { to: '/admin/notifications', label: 'Notifications', icon: Bell },
  { to: '/admin/sentinel', label: 'Reassurance filter', icon: ShieldCheck },
  { to: '/admin/stats', label: 'Usage Stats', icon: LineChart },
  { to: '/admin/seo', label: 'SEO status', icon: Search },
  { to: '/admin/copy', label: 'Homepage Copy', icon: FileText },
  { to: '/admin/attractions', label: 'Attractions', icon: BarChart3 },
  { to: '/admin/media', label: 'Media Library', icon: Image },
  { to: '/admin/events', label: 'Events', icon: Calendar },
  { to: '/admin/scraper', label: 'Event Scraper', icon: Rss },
  { to: '/admin/awards', label: 'Awards', icon: Trophy },
  { to: '/admin/finalists', label: '2026 Finalists', icon: Clapperboard },
  { to: '/admin/inspirations', label: 'AI Inspirations', icon: Sparkles },
  { to: '/admin/members', label: 'Members', icon: Sparkles },
  { to: '/admin/partner-applications', label: 'Partner Applications', icon: Briefcase },
  { to: '/admin/trade-pages', label: 'Trade Pages', icon: BookText },
  { to: '/admin/partner-resources', label: 'Resource Queue', icon: Globe },
  { to: '/admin/payments', label: 'Payments', icon: PoundSterling },
  { to: '/admin/attraction-edits', label: 'Listing edits', icon: FileText },
  { to: '/admin/packages', label: 'Packages', icon: Package },
  { to: '/admin/county-locations', label: 'County Locations', icon: MapPin },
  { to: '/admin/digest', label: 'Weekly Digest', icon: FileText },
  { to: '/admin/newsletter', label: 'Newsletter', icon: Mail },
  { to: '/admin/subscribers', label: 'Subscribers', icon: Users },
  { to: '/admin/costs', label: 'Running costs', icon: PoundSterling, ownerOnly: true },
  { to: '/admin/admins', label: 'Admin team', icon: Shield, ownerOnly: true },
  { to: '/admin/audit-log', label: 'Audit log', icon: Activity, ownerOnly: true },
];

function NavItem({ to, label, icon: Icon, end, badge, onNav }) {
  return (
    <NavLink to={to} end={end} onClick={onNav} data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? 'bg-forest text-base' : 'text-charcoal/80 hover:bg-admin'}`}>
      <Icon size={16}/>
      <span className="flex-1">{label}</span>
      {badge != null && badge > 0 && (
        <span data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}-badge`} className="bg-heritage text-base text-[10px] font-bold rounded-full px-2 py-0.5 leading-tight">{badge}</span>
      )}
    </NavLink>
  );
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const [pendingAwards, setPendingAwards] = useState(0);
  const [pendingInspirations, setPendingInspirations] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('gl_admin_token');
    if (!token) { navigate('/admin/login'); return; }
    api.get('/auth/me').catch(() => {
      localStorage.removeItem('gl_admin_token');
      navigate('/admin/login');
    });
    api.get('/auth/me').then((r) => localStorage.setItem('gl_admin_role', r.data?.role || 'admin')).catch(() => {});
    api.get('/admin/stats').then((r) => setPendingAwards(r.data?.awards_nominees_pending || 0)).catch(() => {});
    api.get('/admin/inspirations?status=pending').then((r) => setPendingInspirations(r.data?.items?.length || 0)).catch(() => {});
    api.get('/admin/notifications?unread_only=true').then((r) => setUnreadNotifications(r.data?.unread || 0)).catch(() => {});
  }, [navigate]);

  function logout() {
    localStorage.removeItem('gl_admin_token');
    localStorage.removeItem('gl_admin_role');
    navigate('/admin/login');
  }

  const email = localStorage.getItem('gl_admin_email') || '';
  const role = localStorage.getItem('gl_admin_role') || 'admin';
  const isOwner = role === 'owner';
  const visibleNav = NAV.filter((item) => !item.ownerOnly || isOwner);

  const badgeFor = (label) => {
    if (label === 'Awards') return pendingAwards;
    if (label === 'AI Inspirations') return pendingInspirations;
    if (label === 'Notifications') return unreadNotifications;
    return 0;
  };

  const totalAlerts = pendingAwards + pendingInspirations + unreadNotifications;

  return (
    <div className="min-h-screen bg-admin">
      <BackendHealthBanner />
      {/* Mobile top bar — hidden on lg+ */}
      <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-charcoal/10 px-4 h-14 flex items-center gap-3">
        <button onClick={() => setDrawerOpen(true)} data-testid="admin-mobile-menu-toggle"
          className="p-2 -ml-2 rounded-lg hover:bg-admin" aria-label="Open menu">
          <Menu size={22} />
        </button>
        <Link to="/" className="flex-1 truncate"><BrandLogo size="sm" suffix={<span className="text-charcoal/40 text-xs ml-1 font-sans">Admin</span>} /></Link>
        {totalAlerts > 0 && (
          <span data-testid="admin-mobile-pending-pill" className="bg-heritage text-base text-[10px] font-bold rounded-full px-2 py-0.5">{totalAlerts}</span>
        )}
      </header>

      {/* Slide-in drawer (mobile) + backdrop */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-charcoal/50" onClick={() => setDrawerOpen(false)} data-testid="admin-drawer-backdrop" />
      )}
      <aside data-testid="admin-mobile-drawer"
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-72 bg-white border-r border-charcoal/10 flex flex-col transition-transform duration-200 ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-5 border-b border-charcoal/10 flex items-center justify-between">
          <div>
            <Link to="/" className="inline-flex"><BrandLogo size="md" /></Link>
            <div className="label text-charcoal/50 mt-1">Admin</div>
          </div>
          <button onClick={() => setDrawerOpen(false)} data-testid="admin-drawer-close"
            className="p-2 rounded-lg hover:bg-admin" aria-label="Close menu"><X size={20} /></button>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {visibleNav.map((item) => <NavItem key={item.to} {...item} badge={badgeFor(item.label)} onNav={() => setDrawerOpen(false)} />)}
        </nav>
        <div className="p-4 border-t border-charcoal/10">
          <div className="px-3 py-1 text-xs text-charcoal/60 break-all">{email} {isOwner && <span className="ml-1 text-[10px] uppercase tracking-wide text-heritage font-bold">· Owner</span>}</div>
          <button onClick={logout} data-testid="admin-logout-mobile"
            className="w-full mt-1 flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-charcoal/70 hover:bg-admin">
            <LogOut size={16}/> Sign out
          </button>
        </div>
      </aside>

      {/* Desktop sidebar — visible from lg up */}
      <div className="flex">
        <aside className="hidden lg:flex w-64 bg-white border-r border-charcoal/10 flex-col sticky top-0 h-screen">
          <div className="p-6 border-b border-charcoal/10">
            <Link to="/" className="inline-flex"><BrandLogo size="md" /></Link>
            <div className="label text-charcoal/50 mt-1">Admin {isOwner && <span className="ml-1 text-[10px] uppercase tracking-wide text-heritage font-bold">· Owner</span>}</div>
          </div>
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {visibleNav.map((item) => <NavItem key={item.to} {...item} badge={badgeFor(item.label)} />)}
          </nav>
          <div className="p-4 border-t border-charcoal/10">
            <div className="px-4 py-2 text-xs text-charcoal/60 break-all">{email}</div>
            <button onClick={logout} data-testid="admin-logout"
              className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-charcoal/70 hover:bg-admin">
              <LogOut size={16}/> Sign out
            </button>
          </div>
        </aside>
        <main className="flex-1 p-4 sm:p-6 lg:p-12 max-w-full overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
